import {
  APIComponentAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { Node } from 'graph/types';
import { Edge, uniqueId, createRefTypeInfoCache } from 'graph/edge';
import { NodeCollection } from 'graph/NodeCollection';
import { GraphCollection } from './GraphCollection';
import { graphUtil as GraphUtil } from 'graph/graphUtil';
import * as profiling from '@ardoq/profiling';
import { action$, ofType } from '@ardoq/rxbeach';
import {
  notifyFiltersChanged,
  setActivePerspective,
} from 'streams/filters/FilterActions';
import { references$ } from 'streams/references/references$';
import { components$ } from 'streams/components/components$';
import { sort$ } from 'streams/context/sort$';
import { locale$ } from 'streams/locale$';
import { BackboneEvents } from 'BackboneEvents';
import { workspaceChanges$ } from 'streams/context/context$';
import { once, throttle } from 'lodash';
import { createComponentNode, createNode } from './createNode';
import { createComponentTypeInfoCache } from './componentTypeInfo';
import {
  EMPTY_GRAPH_MODEL_DATA,
  GraphModelData,
  sortedGraphComponents$,
} from './graphModelData';
import { componentTypesByWorkspaceId$ } from 'streams/models/componentTypesByWorkspaceId$';
import { referenceTypesByWorkspaceId$ } from 'streams/models/referenceTypesByWorkspaceId$';
import { fields$ } from 'streams/fields/fields$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { tags$ } from 'streams/tags/tags$';
import { persistentWorkspaces$ } from 'streams/workspaces/workspaces$';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { combineLatest } from 'rxjs';
import { referenceOperations } from '@ardoq/reference-operations';
import { filterCache } from 'collections/filterCache';
import { getDisplayLabel } from 'modelInterface/util';
import { referenceLabelFilterField$ } from 'streams/filters/referenceLabelFilterField$';
import { getCurrentIncludedInContext } from 'streams/filters/includedInContext$';

export type HierarchicGraphShape = ReturnType<typeof EmptyGraph>;

function BuildGraph(
  rootNodeCollection: NodeCollection,
  nodeCollection: NodeCollection,
  edgeCollection: GraphCollection<Edge>,
  graphModelData: GraphModelData
) {
  const compMap = new Map<ArdoqId, Node<APIComponentAttributes>>();
  const edges: Edge[] = [];
  const workspaceMap = new Map<ArdoqId, Node<APIWorkspaceAttributes>>();
  const getComponentTypeInfo = createComponentTypeInfoCache(graphModelData);

  // clean up old nodes/edges
  edgeCollection.clear({ ignoreNodeUpdates: true });
  rootNodeCollection.clear({ ignoreNodeUpdates: true });
  nodeCollection.clear({ ignoreNodeUpdates: true });

  graphModelData.allComponents.forEach(function (compAttributes) {
    const workspaceId = compAttributes.rootWorkspace;

    if (!graphModelData.includedComponentIds.has(compAttributes._id)) {
      return;
    }
    if (!workspaceMap.get(workspaceId)) {
      const workspaceData = graphModelData.workspacesById[workspaceId];
      if (!workspaceData) {
        return;
      }
      workspaceMap.set(
        workspaceId,
        createNode({
          dataModel: workspaceData,
        })
      );
    }

    compMap.set(
      compAttributes._id,
      createComponentNode({
        dataModel: compAttributes,
        getComponentTypeInfo,
      })
    );
  });

  const getRefTypeInfo = createRefTypeInfoCache(graphModelData);

  const { value, includeTime } = graphModelData.referenceLabelFilter;

  const labelField = graphModelData.labelField;

  graphModelData.allReferences
    .filter(reference => graphModelData.includedReferenceIds.has(reference._id))
    .forEach(ref => {
      const sourceNode = compMap.get(ref.source);
      const targetNode = compMap.get(ref.target);
      if (!sourceNode || !targetNode) return;

      const { referenceType, urlFields } = getRefTypeInfo(ref);
      edges.push(
        new Edge({
          uniqueId: uniqueId(),
          reference: ref,
          referenceType,
          filterColor: filterCache.getColor(ref._id),
          displayLabel:
            getDisplayLabel(
              ref,
              labelField,
              referenceType,
              value,
              !includeTime
            ) || '',
          hasUrlField: referenceOperations.hasURLFields(ref, urlFields),
          sourceNode,
          targetNode,
        })
      );
    });

  // building hierarchy
  compMap.forEach(comp => {
    const parentId = comp.dataModel.parent;
    const parent = parentId && compMap.get(parentId);
    if (parent) {
      parent.children.add(comp);
      comp.setParent(parent);
    } else if (!parentId) {
      const workspaceId = comp.dataModel.rootWorkspace;
      const workspaceNode = workspaceMap.get(workspaceId);
      if (workspaceNode) {
        workspaceNode.children.add(comp);
        comp.setParent(workspaceNode);
      }
    }
  });

  rootNodeCollection.add(workspaceMap);
  nodeCollection.add(compMap);
  nodeCollection.add(workspaceMap);

  edgeCollection.add(edges);

  return compMap;
}

function EmptyGraph() {
  return {
    rootNodes: new NodeCollection(),
    nodes: new NodeCollection(),
    edges: new GraphCollection<Edge>(),
    activeNodes: {},
  };
}

type GetGraphBaseArgs = {
  maxDegreesIncoming?: number;
  maxDegreesOutgoing?: number;
  includeParents?: boolean;
};
type GetGraphByWorkspaceArgs = GetGraphBaseArgs & { workspaceId: ArdoqId };
type GetGraphByComponentArgs = GetGraphBaseArgs & {
  componentId: ArdoqId;
};
type Options = { degreesOfRelationsship: number; types?: unknown };
class HierarchicGraph extends BackboneEvents {
  rootNodeCollection = new NodeCollection();
  nodeCollection = new NodeCollection();
  edgeCollection = new GraphCollection<Edge>();

  dirty = true;
  compMap = new Map();
  graphModelData: GraphModelData = EMPTY_GRAPH_MODEL_DATA;

  _options: Options = { degreesOfRelationsship: 3 };

  rebuild = profiling.functionWithTransaction(
    'hierarchic graph rebuild',
    500,
    () => {
      // Garbage collect old compMap
      this.compMap.clear();
      this.graphModelData = {
        ...this.graphModelData,
        ...getCurrentIncludedInContext(),
      };
      this.compMap = BuildGraph(
        this.rootNodeCollection,
        this.nodeCollection,
        this.edgeCollection,
        this.graphModelData
      );
      this.dirty = false;
    },
    profiling.Team.INSIGHT
  );
  unthrottledRebuild = this.rebuild;

  throttledRebuild = throttle(
    () => {
      if (this.dirty) {
        this.rebuild();
      }
    },
    150,
    {
      leading: true,
    }
  );

  types = this._options.types;

  constructor() {
    super();
    components$.subscribe(this.invalidateGraph);
    action$
      .pipe(ofType(setActivePerspective, notifyFiltersChanged))
      .subscribe(this.invalidateGraph);

    references$.subscribe(this.invalidateGraph);

    workspaceChanges$.subscribe(this.invalidateGraph);

    sort$.subscribe(this.invalidateGraph);

    locale$.subscribe(this.invalidateGraph);

    combineLatest({
      componentTypesByWorkspaceId: componentTypesByWorkspaceId$,
      referenceTypesByWorkspaceId: referenceTypesByWorkspaceId$,
      fields: fields$,
      tags: tags$,
      workspaces: persistentWorkspaces$,
      orgUsers: orgUsers$,
      sortedComponents: sortedGraphComponents$,
      references: references$,
      locale: locale$,
      i18nLocale: i18nLocale$,
      referenceLabelFilterField: referenceLabelFilterField$,
    }).subscribe(
      ({
        componentTypesByWorkspaceId,
        referenceTypesByWorkspaceId,
        fields,
        tags,
        workspaces,
        orgUsers,
        sortedComponents,
        references,
        locale,
        i18nLocale,
        referenceLabelFilterField,
      }) => {
        this.graphModelData = {
          ...this.graphModelData,
          componentTypesByWorkspaceId,
          referenceTypesByWorkspaceId,
          fieldsByComponentType: fields.byComponentType,
          fieldsByReferenceType: fields.byReferenceType,
          workspacesById: workspaces.byId,
          orgUsersById: orgUsers.byId,
          allComponents: sortedComponents,
          allReferences: references.models,
          allTags: tags.models,
          allFields: fields.models,
          referenceLabelFilter: referenceLabelFilterField.referenceLabelFilter,
          labelField: referenceLabelFilterField.labelField,
          locale,
          i18nLocale,
        };
      }
    );
  }

  invalidateGraph = () => {
    this.dirty = true;
  };

  // Public API

  getFullGraph = () => {
    this.throttledRebuild();
    return {
      rootNodes: this.rootNodeCollection,
      nodes: this.nodeCollection,
      edges: this.edgeCollection,
      activeNodes: {},
    };
  };

  getGraphByWorkspace = ({
    workspaceId,
    maxDegreesIncoming = 3,
    maxDegreesOutgoing = 3,
    includeParents = true,
  }: GetGraphByWorkspaceArgs) => {
    const allRootNodes = new NodeCollection();
    const allNodes = new NodeCollection();
    const allEdges = new GraphCollection<Edge>();
    const activeNodes = {};

    if (!workspaceId) {
      return EmptyGraph();
    }
    this.throttledRebuild();

    const workspaceComponents = this.graphModelData.workspacesById[workspaceId]
      ? this.graphModelData.allComponents.filter(
          component => component.rootWorkspace === workspaceId
        )
      : [];

    workspaceComponents
      .filter(
        component =>
          !component.parent &&
          this.graphModelData.includedComponentIds.has(component._id)
      )
      .forEach(component => {
        const { rootNodes, nodes, edges } = this.getGraphByComponent({
          componentId: component._id,
          maxDegreesIncoming,
          maxDegreesOutgoing,
          includeParents,
        });

        allRootNodes.add(rootNodes);
        allNodes.add(nodes);
        allEdges.add(edges);
      });

    return {
      rootNodes: allRootNodes,
      nodes: allNodes,
      edges: allEdges,
      activeNodes,
    };
  };

  getGraphByComponent = ({
    componentId,
    maxDegreesIncoming = 3,
    maxDegreesOutgoing = 3,
    includeParents = true,
  }: GetGraphByComponentArgs) => {
    this.throttledRebuild();
    const rootNode = this.compMap.get(componentId);
    const activeNodes: Record<string, boolean> = {};
    const includedNodes = new Set<Node>();
    const includedEdges = new Set<Edge>();

    if (!componentId || !rootNode) {
      return EmptyGraph();
    }

    activeNodes[rootNode.id] = true;
    if (maxDegreesIncoming !== maxDegreesOutgoing) {
      GraphUtil.getOutgoingGraphSubset({
        node: rootNode,
        nodeSet: includedNodes,
        edgeSet: includedEdges,
        maxDegrees: maxDegreesOutgoing,
        includeParents: includeParents,
      });
      // Get incoming nodes on the outgoing subset
      const incomingNodes = new Set<Node>();
      includedNodes.forEach(node => {
        GraphUtil.getIncomingGraphSubset({
          node,
          nodeSet: incomingNodes,
          edgeSet: includedEdges,
          maxDegrees: maxDegreesIncoming,
          includeParents: includeParents,
        });
      });
      incomingNodes.forEach(node => includedNodes.add(node));
    } else {
      GraphUtil.getGraphSubset({
        node: rootNode,
        nodeSet: includedNodes,
        edgeSet: includedEdges,
        maxDegreesIncoming,
        maxDegreesOutgoing: maxDegreesIncoming,
        includeParents: includeParents,
      });
    }

    GraphUtil.ensureAllEdgesAreIncluded({
      nodeSet: includedNodes,
      edgeSet: includedEdges,
      allEdges: this.edgeCollection,
    });

    return {
      rootNodes: new NodeCollection([rootNode]),
      nodes: new NodeCollection(Array.from(includedNodes)),
      edges: new GraphCollection<Edge>(Array.from(includedEdges)),
      activeNodes: activeNodes,
    };
  };
}

/**
 * Built on first use rather than on module load. The constructor subscribes, and several of those
 * subscriptions read a model interface on their first emission — `startApplication` calls
 * `initializeModelInterfaces` from a function body, so nothing is registered while modules are
 * still evaluating. Constructing here made every page that loads this chunk read an unregistered
 * stub, whether or not it draws a graph.
 */
export const getHierarchicGraph: () => HierarchicGraph = once(
  () => new HierarchicGraph()
);
